import { type FC, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Loading } from "src/components";
import { Routes } from "src/routes";
import { usePayoutPaymentMutation } from "src/serverApi";
import { useTranslation } from "src/translations";
import type { DocumentId, PayoutPaymentGatewayUri } from "src/types";
import { useComponentDidMount } from "src/utils";
import { logger } from "../logger";
import { PayError } from "./PayError";
import { PayoutTransferDetails } from "./PayoutTransferDetails";

type PayoutTransferDetailsLoaderProps = Readonly<{
    orderDocumentId: DocumentId;
}>;

export const PayoutTransferDetailsLoader: FC<PayoutTransferDetailsLoaderProps> = ({ orderDocumentId }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [pay, { isLoading, isError }] = usePayoutPaymentMutation();
    const [transferDetails, setTransferDetails] = useState<PayoutPaymentGatewayUri>();
    const hasRequestedPayment = useRef(false);

    const requestPayment = useCallback(() => {
        pay({ orderDocumentId, returnUri: `${window.location.origin}${Routes.orders.path}` })
            .unwrap()
            .then(setTransferDetails)
            .catch((error) => {
                logger.error("PayoutTransferDetailsLoader", error);
            });
    }, [orderDocumentId, pay]);

    useComponentDidMount(() => {
        if (hasRequestedPayment.current) {
            return;
        }
        hasRequestedPayment.current = true;
        requestPayment();
    });

    if (transferDetails) {
        return <PayoutTransferDetails details={transferDetails} />;
    }

    if (isError) {
        return (
            <div className="grid justify-items-center gap-4">
                <PayError error={t("orders.pay.gateway.error", { method: "Payout" })} />
                <div className="flex gap-4">
                    <Button kind="outline" onClick={() => navigate(Routes.orderPay.fillPathParams({ orderDocumentId }))}>
                        {t("orders.pay.gateway.back")}
                    </Button>
                    <Button isLoading={isLoading} onClick={requestPayment}>
                        {t("orders.pay.gateway.retry")}
                    </Button>
                </div>
            </div>
        );
    }

    return <Loading className="h-32" />;
};
